import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { 
  Grid, List, FileUp, Search, 
  ArrowUpDown, ArrowUp,
  ArrowDown, X, Check, Filter
} from 'lucide-react';
import { useFileStore } from '../stores/file-store';
import FileGrid from '../components/files/FileGrid';
import FileList from '../components/files/FileList';
// import { FileType as FileTypeEnum } from '../types';
import UploadModal from '../components/files/UploadModal';
import { cn } from '../utils/cn';
import { useI18n } from '../i18n';
import { useDelayedLoading } from '../hooks/useDelayedLoading';
import { SkeletonGrid, SkeletonList } from '../components/shared/Skeleton';

const FilesPage: React.FC = () => {
  const { filteredFiles, favoriteFiles, recentFiles, filterFilesByType, sortFiles, resetSearch, fetchFiles, fetchFavorites, fetchRecent, isLoading } = useFileStore();
  const location = useLocation();
  const isStarredPage = location.pathname === '/uadmin/starred';
  const isRecentPage = location.pathname === '/uadmin/recent';
  const { t } = useI18n();
  
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [filterMenuOpen, setFilterMenuOpen] = useState(false);
  const [sortMenuOpen, setSortMenuOpen] = useState(false);
  const [activeFilter, setActiveFilter] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortCriteria, setSortCriteria] = useState<'name' | 'date' | 'size'>('date');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [showUploadModal, setShowUploadModal] = useState(false);
  
  // Use delayed loading hook to implement 500ms threshold principle
  // This prevents brief loading flickers for fast loads while showing
  // skeleton screens for slower loads (Requirements: 2.1, 2.2, 3.1)
  const { showLoading } = useDelayedLoading(isLoading);
  
  React.useEffect(() => {
    if (isStarredPage) {
      // 收藏页面：获取收藏的文件
      fetchFavorites();
    } else if (isRecentPage) {
      // 最近页面：获取最近访问的文件
      fetchRecent();
    } else {
      // 全部文件页面：获取所有文件
      fetchFiles();
    }
  }, [fetchFiles, fetchFavorites, fetchRecent, isStarredPage, isRecentPage]);
  
  const fileTypes: { type: string; label: string }[] = [
    { type: 'all', label: t('fileType.all') },
    { type: 'document', label: t('fileType.document') },
    { type: 'image', label: t('fileType.image') },
    { type: 'video', label: t('fileType.video') },
    { type: 'audio', label: t('fileType.audio') },
    { type: 'pdf', label: t('fileType.pdf') },
    { type: 'spreadsheet', label: t('fileType.spreadsheet') },
    { type: 'presentation', label: t('fileType.presentation') },
  ];
  
  const handleFilterChange = (type: string) => {
    setActiveFilter(type);
    filterFilesByType(type);
    setFilterMenuOpen(false);
  };
  
  const handleSortChange = (criteria: 'name' | 'date' | 'size') => {
    if (criteria === sortCriteria) {
      // Toggle direction if same criteria
      const newDirection = sortDirection === 'asc' ? 'desc' : 'asc';
      setSortDirection(newDirection);
      sortFiles(criteria, newDirection);
    } else {
      // New criteria, default to descending
      setSortCriteria(criteria);
      setSortDirection('desc');
      sortFiles(criteria, 'desc');
    }
    setSortMenuOpen(false);
  };
  
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
    const query = e.target.value;
    if (query.length >= 2) {
      useFileStore.getState().setSearchQuery(query);
      useFileStore.getState().search();
    } else if (query.length === 0) {
      useFileStore.getState().resetSearch();
    }
  };
  
  const getSortIcon = (criteria: 'name' | 'date' | 'size') => {
    if (criteria !== sortCriteria) return null;
    return sortDirection === 'asc' ? <ArrowUp size={14} /> : <ArrowDown size={14} />;
  };
  
  // 根据当前页面获取标题
  const getPageTitle = () => {
    if (isStarredPage) return t('starred');
    if (isRecentPage) return t('recent');
    return t('filesTitle');
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 md:gap-4">
        <h1 className="text-2xl font-bold text-gray-800">{getPageTitle()}</h1>

        <div className="flex items-center space-x-2 overflow-x-auto">
          <div className="relative flex-1 md:w-64">
            <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
              <Search size={16} className="text-gray-400" />
            </div>
            <input
              type="text"
              placeholder={t('searchFilesPlaceholder')}
              className="w-full pl-10 pr-4 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
              value={searchQuery}
              onChange={handleSearchChange}
            />
          </div>
          
          <div className="relative">
            <button
              onClick={() => setFilterMenuOpen(!filterMenuOpen)}
              className="flex items-center h-9 px-2 md:px-3 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-teal-500"
            >
              <Filter size={16} className="mr-0 md:mr-2" />
              <span className="hidden md:inline">{t('filter')}</span>
            </button>
            
            {filterMenuOpen && (
              <div className="absolute right-0 z-10 mt-2 w-56 origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5">
                <div className="py-1">
                  {fileTypes.map((fileType) => (
                    <button
                      key={fileType.type}
                      onClick={() => handleFilterChange(fileType.type)}
                      className={cn(
                        "flex items-center justify-between w-full px-4 py-2 text-sm",
                        activeFilter === fileType.type
                          ? "bg-teal-50 text-teal-700"
                          : "text-gray-700 hover:bg-gray-50"
                      )}
                    >
                      {fileType.label}
                      {activeFilter === fileType.type && (
                        <Check size={16} className="text-teal-600" />
                      )}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
          
          <div className="relative">
            <button
              onClick={() => setSortMenuOpen(!sortMenuOpen)}
              className="flex items-center h-9 px-2 md:px-3 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-teal-500"
            >
              <ArrowUpDown size={16} className="mr-0 md:mr-2" />
              <span className="hidden md:inline">{t('sort')}</span>
            </button>
            
            {sortMenuOpen && (
              <div className="absolute right-0 z-10 mt-2 w-56 origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5">
                <div className="py-1">
                  <button
                    onClick={() => handleSortChange('name')}
                    className={cn(
                      "flex items-center justify-between w-full px-4 py-2 text-sm",
                      sortCriteria === 'name'
                        ? "bg-teal-50 text-teal-700"
                        : "text-gray-700 hover:bg-gray-50"
                    )}
                  >
                    <span>{t('sortName')}</span>
                    {getSortIcon('name')}
                  </button>
                  <button
                    onClick={() => handleSortChange('date')}
                    className={cn(
                      "flex items-center justify-between w-full px-4 py-2 text-sm",
                      sortCriteria === 'date'
                        ? "bg-teal-50 text-teal-700"
                        : "text-gray-700 hover:bg-gray-50"
                    )}
                  >
                    <span>{t('sortDate')}</span>
                    {getSortIcon('date')}
                  </button>
                  <button
                    onClick={() => handleSortChange('size')}
                    className={cn(
                      "flex items-center justify-between w-full px-4 py-2 text-sm",
                      sortCriteria === 'size'
                        ? "bg-teal-50 text-teal-700"
                        : "text-gray-700 hover:bg-gray-50"
                    )}
                  >
                    <span>{t('sortSize')}</span>
                    {getSortIcon('size')}
                  </button>
                </div>
              </div>
            )}
          </div>
          
          <div className="flex border border-gray-300 rounded-md">
            <button
              onClick={() => setViewMode('grid')}
              className={cn(
                "p-2 h-9 text-sm",
                viewMode === 'grid'
                  ? "bg-teal-50 text-teal-700 border-r border-gray-300"
                  : "bg-white text-gray-700 hover:bg-gray-50 border-r border-gray-300"
              )}
            >
              <Grid size={16} />
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={cn(
                "p-2 h-9 text-sm",
                viewMode === 'list'
                  ? "bg-teal-50 text-teal-700"
                  : "bg-white text-gray-700 hover:bg-gray-50"
              )}
            >
              <List size={16} />
            </button>
          </div>
          
          <button
            onClick={() => setShowUploadModal(true)}
            className="h-9 px-2 md:px-4 text-sm font-medium text-white bg-teal-600 rounded-md hover:bg-teal-700 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-opacity-50 flex items-center"
          >
            <FileUp size={16} className="mr-0 md:mr-2" />
            <span className="hidden md:inline">{t('upload')}</span>
          </button>
        </div>
      </div>
      
      {activeFilter !== 'all' && (
        <div className="flex items-center">
          <div className="flex items-center px-3 py-1 text-sm font-medium text-gray-700 bg-gray-100 rounded-md">
            <span>Filter: {activeFilter}</span>
            <button
              onClick={() => {
                setActiveFilter('all');
                resetSearch();
              }}
              className="ml-2 text-gray-500 hover:text-gray-700"
            >
              <X size={14} />
            </button>
          </div>
        </div>
      )}
      
      <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
        <div className="p-4">
          {showLoading ? (
            // Show skeleton loading after 500ms threshold (Requirements: 2.1, 2.2, 3.1)
            // Uses SkeletonGrid for grid view and SkeletonList for list view
            viewMode === 'grid' ? (
              <SkeletonGrid count={8} />
            ) : (
              <SkeletonList count={5} />
            )
          ) : viewMode === 'grid' ? (
              <FileGrid 
              files={isStarredPage ? favoriteFiles : isRecentPage ? (recentFiles || []) : filteredFiles}
                emptyMessage={isStarredPage ? t('noStarredTip') : isRecentPage ? '暂无最近访问的文件' : t('noFilesFound')}
            />
          ) : (
            <FileList files={isStarredPage ? favoriteFiles : isRecentPage ? (recentFiles || []) : filteredFiles} />
          )}
        </div>
      </div>
      
      {showUploadModal && (
        <UploadModal onClose={() => setShowUploadModal(false)} />
      )}
    </div>
  );
};

export default FilesPage;